import { useEffect, useState } from 'react';
import { Edit } from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/products';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ProductPricing({ product, onSave }) {
  const [editMode, setEditMode] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const showOverlay = (message) => {
    setOverlayMessage(message);
    setIsOverlayOpen(true);
    setTimeout(() => setIsOverlayOpen(false), 3000);
  };

  const [pricing, setPricing] = useState({
    unit_price: product.price.split(' ')[1] || '',
    currency: product.currency,
    pricing_type: product.pricing_type,
    is_refundable: product.is_refundable
  });

  // Sync state when product updates without refreshing
  useEffect(() => {
    setPricing({
      unit_price: product.price.split(' ')[1] || '',
      currency: product.currency,
      pricing_type: product.pricing_type,
      is_refundable: product.is_refundable
    });
  }, [product]);

  const handleSave = async () => {
    try {
      const response = await fetch(`${BASE_URL}/updateProducts/${product.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          pricing: {
            // Added fallback to 0 to prevent NaN errors if left empty
            unit_price: parseFloat(pricing.unit_price) || 0,
            currency: pricing.currency,
            pricing_type: pricing.pricing_type,
            is_refundable: pricing.is_refundable
          }
        })
      });

      if (!response.ok) throw new Error('Failed to update pricing');
      
      setEditMode(false);
      await onSave();
      showOverlay('Pricing updated successfully');
    } catch (error) {
      console.error('Error updating pricing:', error);
      showOverlay('Failed to update pricing: ' + error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-medium text-gray-900">Pricing Details</h3>
        {!editMode ? (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(false)}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Unit Price
          </label>
          {editMode ? (
            <input
              type="number"
              value={pricing.unit_price}
              onChange={(e) => setPricing({ ...pricing, unit_price: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
              {pricing.unit_price}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Currency
          </label>
          {editMode ? (
            <select
              value={pricing.currency}
              onChange={(e) => setPricing({ ...pricing, currency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="HK$">HK$</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="IDR">IDR</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
              {pricing.currency}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pricing Type
          </label>
          {editMode ? (
            <select
              value={pricing.pricing_type}
              onChange={(e) => setPricing({ ...pricing, pricing_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="per_quantity">Per Quantity</option>
              <option value="fixed">Fixed</option>
              <option value="tiered">Tiered</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 capitalize">
              {pricing.pricing_type.replace('_', ' ')}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Refundable
          </label>
          {editMode ? (
            <select
              value={pricing.is_refundable ? 'yes' : 'no'}
              onChange={(e) => setPricing({ ...pricing, is_refundable: e.target.value === 'yes' })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
              {pricing.is_refundable ? 'Yes' : 'No'}
            </div>
          )}
        </div>
      </div>
      {/* Overlay Message */}
      {isOverlayOpen && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm">{overlayMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}